import { makeAutoObservable, runInAction } from "mobx";
import { ApiRequestError, type ApiClient } from "../../platform/api-client";
import { isOpenIncidentState } from "../../domain/status-presentation";
import { preferOperationalIncident } from "./incident-default-selection";
import type {
  DeployableModuleInstance,
  StanModuleAssessment,
} from "../stan-catalogue/stan-catalogue-client";
import type {
  ReadinessStatusSource,
  ReadinessStatusView,
} from "../readiness-status/readiness-status-store";
import {
  incidentBelongsToOperationalScope,
  type IncidentOperationalRelation,
  type OperationalScopeBinding,
} from "./incident-scope-policy";

export interface OperationalIncidentSummary {
  readonly incidentId: string;
  readonly incidentKind: "LOCAL_LUK" | "REGIONAL_PARENT";
  readonly regionalParentIncidentId: string | null;
  readonly incidentNumber: string | null;
  readonly title: string;
  readonly owningOrganizationUnitId: string;
  readonly owningOrganizationUnitLabel?: string | null;
  readonly missionId: string | null;
  readonly locationText: string | null;
  readonly state: string;
  readonly severity: string | null;
  /** Free-text damage/reason captured at incident registration — the card's description line. Present in the `GET /v1/incidents` payload (no response schema strips it). */
  readonly damageOrReason: string | null;
  /**
   * Event type + sub-type labels resolved from `eventCatalogueId` (a hierarchical
   * governed catalogue: item = sub-type, its `parentItemId` = type). Optional
   * because the backend read-model resolve is a separate coordinated increment
   * (the catalogue-items endpoint is admin-gated, so the client can't resolve).
   * When absent the card falls back to the location line.
   */
  readonly eventType?: string | null;
  readonly eventSubtype?: string | null;
  readonly responsibleAuthorityId: string | null;
  readonly operationStartedAt?: string | null;
  /**
   * Why discovery returned this incident to the current account. Drives the
   * work-context narrowing in `incident-scope-policy`; see that module for why
   * an owner-only rule would be wrong. Optional so any response predating the
   * attribute still loads, narrowed rather than widened.
   */
  readonly operationalRelation?: IncidentOperationalRelation;
  readonly updatedAt: string;
  readonly reopenCount: number;
  readonly reopenedAt: string | null;
  readonly reopenReason: string | null;
}

export interface OperationalParticipation {
  readonly participantId: string;
  readonly incidentId: string;
  readonly kind: "EXTERNAL" | "THW";
  readonly organizationUnitId: string | null;
  readonly externalName: string | null;
  readonly role: string | null;
  readonly joinedAt: string;
  readonly leftAt: string | null;
  readonly version: number;
}

export interface OperationalMissionLink {
  readonly incidentId: string;
  readonly missionId: string;
  readonly sourceRequestId: string | null;
  readonly relationType: "DERIVED" | "PRIMARY" | "SUPPORTING";
  readonly linkedBy: string;
  readonly linkedAt: string;
}

export interface OperationalRequestSummary {
  readonly requestId: string;
  readonly requestType: string;
  readonly priority: string;
  readonly status: string;
  readonly receivingOrganizationId: string;
  readonly requestingAuthorityId: string;
  readonly operationalLocation: string | null;
  /**
   * The incident's reported position, captured by the intake map picker and
   * persisted on `assistance_request.operational_latitude/longitude`.
   *
   * `GET /v1/incidents/{id}/operational-context` has always returned these —
   * `operational-context-service.ts` passes the whole `AssistanceRequest`
   * through — but this projection did not declare them, so the coordinates
   * stopped here and the situation map had nothing to place. Declared
   * optional: a request predating the location migration carries neither.
   */
  readonly operationalLatitude?: number | null;
  readonly operationalLongitude?: number | null;
  readonly receivedAt?: string | null;
  readonly updatedAt?: string | null;
  readonly version?: number;
}

export interface RegionalSupportRequest {
  readonly supportRequestId: string;
  readonly regionalIncidentId: string;
  readonly requestedOrganizationUnitId: string;
  readonly requestedOrganizationUnitLabel?: string | null;
  readonly status: "REQUESTED" | "ACCEPTED" | "DECLINED" | "CANCELLED";
  readonly message: string | null;
  readonly localIncidentId: string | null;
  readonly requestedBy: string;
  readonly requestedAt: string;
  readonly responseReason: string | null;
  readonly respondedBy: string | null;
  readonly respondedAt: string | null;
  readonly requirements: readonly RegionalSupportRequirement[];
  readonly version: number;
  readonly updatedAt: string;
}

/** Permission-safe proof that a Local LuK escalation reached its Regionalstelle. */
export interface RegionalCoordinationHandoff {
  readonly escalatedAt: string;
  readonly regionalIncidentNumber: string | null;
  readonly regionalIncidentState: string;
  readonly regionalOrganizationUnitId: string;
  readonly regionalOrganizationUnitLabel?: string | null;
}

function isRegionalCoordinationHandoff(
  value: unknown,
): value is RegionalCoordinationHandoff {
  if (value === null || typeof value !== "object") return false;
  const candidate = value as Partial<RegionalCoordinationHandoff>;
  return typeof candidate.escalatedAt === "string" &&
    Number.isFinite(Date.parse(candidate.escalatedAt)) &&
    (candidate.regionalIncidentNumber === null ||
      typeof candidate.regionalIncidentNumber === "string") &&
    typeof candidate.regionalIncidentState === "string" &&
    candidate.regionalIncidentState.length > 0 &&
    typeof candidate.regionalOrganizationUnitId === "string" &&
    candidate.regionalOrganizationUnitId.length > 0 &&
    (candidate.regionalOrganizationUnitLabel === undefined ||
      candidate.regionalOrganizationUnitLabel === null ||
      typeof candidate.regionalOrganizationUnitLabel === "string");
}

export type RegionalStanMatchStatus = "MATCH" | "ALTERNATIVE" | "NO_MATCH";
export type RegionalAvailabilityStatus =
  | "AVAILABLE"
  | "PARTIALLY_AVAILABLE"
  | "UNAVAILABLE";
export type RegionalReadinessStatus =
  | "READY"
  | "RESTRICTED"
  | "NOT_READY"
  | "UNKNOWN";

export interface RegionalSupportRequirement {
  readonly requirementId: string;
  readonly capabilityRef: string | null;
  readonly outcomeText: string;
  readonly requestedQuantity: number;
  readonly priority: "LOW" | "NORMAL" | "HIGH" | "CRITICAL";
  readonly neededBy: string | null;
  readonly sourceKind: "REQUEST_LINE" | "ASSESSMENT_SUBMISSION";
  readonly sourceRef: string;
  readonly sourceContentHash: string | null;
  readonly stanMatchStatus: RegionalStanMatchStatus | null;
  readonly offeredCapabilityRef: string | null;
  readonly availabilityStatus: RegionalAvailabilityStatus | null;
  readonly readinessStatus: RegionalReadinessStatus | null;
  readonly offeredQuantity: number | null;
  readonly responseNote: string | null;
  readonly localRequirementId: string | null;
}

export interface RegionalCapabilityResponse {
  readonly requirementId: string;
  readonly stanMatchStatus: RegionalStanMatchStatus;
  readonly offeredCapabilityRef: string | null;
  readonly availabilityStatus: RegionalAvailabilityStatus;
  readonly readinessStatus: RegionalReadinessStatus;
  readonly offeredQuantity: number;
  readonly note: string | null;
}

/**
 * Read-only local evidence shown before a supporting LuK responds. The three
 * decisions remain separate: this record proves the structural StAN snapshot
 * and latest M07 status, but deliberately does not claim actual availability.
 */
export interface RegionalSupportEvidenceCandidate {
  readonly moduleInstanceId: string;
  readonly moduleRevision: number;
  readonly organizationUnitId: string;
  readonly unitTypeCode: string;
  readonly unitTypeLabel: string;
  /** English translation authored in the catalogue, when one exists. */
  readonly unitTypeLabelEn: string | null;
  readonly compatibility: StanModuleAssessment["compatibility"];
  readonly assignedPersonnel: number;
  readonly targetPersonnel: number | null;
  readonly assignedAssets: number;
  readonly referenceCodes: readonly string[];
  readonly gaps: readonly string[];
  readonly readinessStatus: string;
  readonly readinessSource: ReadinessStatusSource;
  readonly readinessValidUntil: string | null;
}

export interface OperationalRequirementSummary {
  readonly requirementId: string;
  readonly incidentId: string;
  readonly capabilityRef: string | null;
  readonly outcomeText: string;
  readonly quantity: number;
  readonly neededBy: string | null;
  readonly priority: "LOW" | "NORMAL" | "HIGH" | "CRITICAL";
  readonly sourceKind: "REQUEST_LINE" | "ASSESSMENT_SUBMISSION";
  readonly sourceRef: string;
  readonly state: string;
}

export interface OperationalOrganizationOption {
  readonly unitId: string;
  readonly parentUnitId: string | null;
  readonly unitTypeCode: string;
  readonly label: string;
}

export interface IncidentClosureBlocker {
  readonly code: string;
  readonly sourceModule: string;
  readonly sourceReference: string;
}

export interface IncidentClosureEvidence {
  readonly damageLoss: {
    readonly incidentId: string;
    readonly revision: number;
    readonly disposition: "NONE_REPORTED" | "FOLLOW_UP_ASSIGNED";
    readonly reportReference: string | null;
    readonly followUpOwnerReference: string | null;
    readonly note: string;
    readonly recordedBy: string;
    readonly recordedAt: string;
  } | null;
  readonly situationMapArchive: {
    readonly archiveId: string;
    readonly incidentId: string;
    readonly revision: number;
    readonly contextId: string;
    readonly organizationUnitId: string;
    readonly publicationCount: number;
    readonly contentHash: string;
    readonly archivedBy: string;
    readonly archivedAt: string;
  } | null;
}

export const INCIDENT_ROLE_KEYS = [
  "INCIDENT_COMMANDER",
  "S1_PERSONNEL",
  "S2_SITUATION_REPORTING",
  "S3_OPERATIONS",
  "S4_LOGISTICS",
  "S6_COMMUNICATIONS",
  "ETB_KEEPER",
] as const;

export type IncidentRoleKey = (typeof INCIDENT_ROLE_KEYS)[number];

export interface OperationalIncidentRoleAssignment {
  readonly assignmentId: string;
  readonly incidentId: string;
  readonly accountId: string;
  /** Narrow incident-scoped presentation fields returned only for this appointment. */
  readonly accountDisplayName?: string;
  readonly accountLoginIdentifier?: string;
  readonly accountOrganizationUnitId?: string;
  readonly accountOrganizationUnitLabel?: string;
  readonly roleKey: IncidentRoleKey;
  readonly assignedBy: string;
  readonly reason: string | null;
  readonly startAt: string;
  readonly endAt: string | null;
  readonly status: "ACTIVE" | "ENDED" | "REVOKED";
  readonly version: number;
}

export interface OperationalIncidentRoleCandidate {
  readonly accountId: string;
  readonly displayName?: string;
  readonly loginIdentifier: string;
  readonly organizationUnitId: string;
  readonly organizationUnitLabel?: string;
  readonly eligibleRoleKeys: readonly IncidentRoleKey[];
}

export function hasActiveIncidentRole(
  assignments: readonly OperationalIncidentRoleAssignment[],
  accountId: string,
  roleKey: IncidentRoleKey,
): boolean {
  return accountId !== "" && assignments.some(
    (assignment) =>
      assignment.accountId === accountId &&
      assignment.roleKey === roleKey &&
      assignment.status === "ACTIVE",
  );
}

/**
 * Command nodes are mission-scoped authority records. A broad organization
 * grant may advertise the permission in discovery, but the API also requires
 * the current principal's active Incident Commander appointment for the
 * selected incident. Keep the action hidden unless both conditions hold.
 */
export function canManageIncidentCommandStructure(
  readOnly: boolean,
  hasActiveCommanderAppointment: boolean,
  canDiscoverCommandManagement: boolean,
): boolean {
  return !readOnly &&
    hasActiveCommanderAppointment &&
    canDiscoverCommandManagement;
}

/**
 * A Regional Parent is a consolidated projection. A Regionalstelle technical
 * context drilling into a Local LuK Incident is likewise only observing the
 * owning LuK's authoritative operational record. Normal local-module
 * mutations must stay suppressed in both views; dedicated regional
 * coordination commands are composed separately.
 */
export function isRegionalReadOnlyOperationalView(
  roleCode: string | null | undefined,
  incidentKind: OperationalIncidentSummary["incidentKind"] | null | undefined,
  activeOrganizationUnitId?: string | null,
  owningOrganizationUnitId?: string | null,
): boolean {
  return incidentKind === "REGIONAL_PARENT" ||
    roleCode === "regionalstelle" ||
    (
      incidentKind === "LOCAL_LUK" &&
      activeOrganizationUnitId !== null &&
      activeOrganizationUnitId !== undefined &&
      owningOrganizationUnitId !== null &&
      owningOrganizationUnitId !== undefined &&
      activeOrganizationUnitId !== owningOrganizationUnitId
    );
}

export interface OperationalAccountOption {
  readonly accountId: string;
  readonly loginIdentifier: string;
  readonly organizationUnitId: string;
  readonly personId: string | null;
  readonly state: "CREATED" | "ACTIVE" | "SUSPENDED" | "DEACTIVATED";
}

export interface RegionalTimelineEntry {
  readonly entryId: string;
  readonly sourceIncidentId: string;
  readonly sourceIncidentNumber: string | null;
  readonly sourceIncidentTitle: string;
  readonly sourceOrganizationUnitId: string;
  readonly sourceOrganizationUnitLabel?: string | null;
  readonly sequenceNumber: number;
  readonly entryType: "PROMOTION" | "CORRECTION";
  readonly summary: string;
  readonly text: string;
  readonly effectiveAt: string;
  readonly recordedAt: string;
  readonly sourceType: string;
  readonly sourceReference: string;
}

/**
 * Where the selected incident is, for the surfaces that place it — today the
 * situation map. Assembled from the incident record plus its intake requests;
 * see `OperationalContextStore.incidentLocation`.
 */
export interface OperationalIncidentLocation {
  readonly incidentId: string;
  readonly incidentNumber: string | null;
  readonly title: string;
  readonly state: string;
  readonly address: string | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly source: "INCIDENT_RECORD" | "INTAKE_REQUEST" | null;
  readonly requestId: string | null;
  readonly requestVersion: number | null;
  readonly reportedAt: string | null;
  readonly updatedAt: string | null;
}

export interface OperationalIncidentContextView {
  readonly incident: OperationalIncidentSummary;
  readonly participations: readonly OperationalParticipation[];
  readonly assistanceRequests: readonly OperationalRequestSummary[];
  readonly missionLinks: readonly OperationalMissionLink[];
  readonly lastConfirmedAt: string;
}

export interface OperationalContextSelection {
  readonly incidentId: string | null;
  readonly participationId: string | null;
  readonly assistanceRequestId: string | null;
  readonly requirementId: string | null;
  readonly missionId: string | null;
  readonly operationalNodeId: string | null;
  readonly logbookId: string | null;
}

const EMPTY_SELECTION: OperationalContextSelection = Object.freeze({
  incidentId: null,
  participationId: null,
  assistanceRequestId: null,
  requirementId: null,
  missionId: null,
  operationalNodeId: null,
  logbookId: null,
});

/**
 * The selected incident survives a page reload (per tab and principal). Only
 * stored — everything else is re-derived from the server, and features that
 * read the selection re-authorize on every request anyway. Principal scoping
 * prevents a shared app-store instance from carrying one account's context
 * across logout/login.
 */
const SELECTED_INCIDENT_STORAGE_PREFIX = "thw.operational-context.incident:";

/**
 * These technical administration contexts intentionally retain the server's
 * broader incident projection. Every operational context is narrowed to the
 * concrete organization selected in the session before an incident id can
 * reach a module store.
 */
const CROSS_ORGANIZATION_INCIDENT_ROLES = new Set([
  "superuser",
  "super_admin",
  "lv_admin",
  "rst_admin",
  "master_data_admin",
  "auditor",
  "access_administrator",
]);

function scopedOrganizationUnitId(
  roleCode: string | null,
  organizationUnitId: string | null,
): string | null {
  if (organizationUnitId === null ||
    (roleCode !== null && CROSS_ORGANIZATION_INCIDENT_ROLES.has(roleCode))) {
    return null;
  }
  return organizationUnitId;
}

function selectedIncidentStorageKey(principalId: string): string {
  return `${SELECTED_INCIDENT_STORAGE_PREFIX}${encodeURIComponent(principalId)}`;
}

function readStoredIncidentId(principalId: string): string | null {
  try {
    return globalThis.sessionStorage.getItem(
      selectedIncidentStorageKey(principalId),
    );
  } catch {
    return null;
  }
}

function writeStoredIncidentId(
  principalId: string | null,
  incidentId: string | null,
): void {
  if (principalId === null) return;
  try {
    const key = selectedIncidentStorageKey(principalId);
    if (incidentId === null) {
      globalThis.sessionStorage.removeItem(key);
    } else {
      globalThis.sessionStorage.setItem(key, incidentId);
    }
  } catch {
    // Storage may be unavailable (private mode, quotas); selection then simply
    // does not survive a reload.
  }
}

export class OperationalContextStore {
  /** Set once `/v1/accounts` refuses, so the directory is not re-requested. */
  private accountDirectoryDenied = false;
  incidents: readonly OperationalIncidentSummary[] = [];
  regionalChildren: readonly OperationalIncidentSummary[] = [];
  regionalSupportRequests: readonly RegionalSupportRequest[] = [];
  /** Requests originating from the selected Local LuK Incident. */
  outgoingRegionalSupport: readonly RegionalSupportRequest[] = [];
  outgoingRegionalSupportLoading = false;
  outgoingRegionalSupportError: string | null = null;
  regionalCoordinationHandoff: RegionalCoordinationHandoff | null = null;
  regionalCoordinationHandoffLoading = false;
  regionalCoordinationHandoffError: string | null = null;
  incomingRegionalSupport: readonly RegionalSupportRequest[] = [];
  incomingRegionalSupportOrganizationUnitId: string | null = null;
  regionalSupportDetailRequest: RegionalSupportRequest | null = null;
  regionalSupportDetailSource: "INCOMING" | "REGIONAL" | null = null;
  regionalSupportDetailIncidentId: string | null = null;
  regionalSupportEvidenceRequestId: string | null = null;
  regionalSupportEvidence: readonly RegionalSupportEvidenceCandidate[] = [];
  regionalSupportEvidenceLoading = false;
  regionalSupportEvidenceError: string | null = null;
  regionalInboxLoading = false;
  regionalInboxError: string | null = null;
  regionalTimeline: readonly RegionalTimelineEntry[] = [];
  regionalTimelineLoading = false;
  regionalTimelineError: string | null = null;
  regionalGapRequirements: readonly OperationalRequirementSummary[] = [];
  regionalGapRequirementsLoading = false;
  regionalGapRequirementsError: string | null = null;
  organizationOptions: readonly OperationalOrganizationOption[] = [];
  closureBlockers: readonly IncidentClosureBlocker[] = [];
  closureBlockersLoading = false;
  closureBlockersError: string | null = null;
  closureEvidence: IncidentClosureEvidence | null = null;
  closureEvidenceLoading = false;
  closureEvidenceError: string | null = null;
  roleAssignments: readonly OperationalIncidentRoleAssignment[] = [];
  roleCandidateOptions: readonly OperationalIncidentRoleCandidate[] = [];
  accountOptions: readonly OperationalAccountOption[] = [];
  context: OperationalIncidentContextView | null = null;
  selection: OperationalContextSelection = EMPTY_SELECTION;
  loading = false;
  error: string | null = null;
  notice: string | null = null;
  scopeDeniedIncidentId: string | null = null;
  private contextRequestToken = 0;
  /**
   * One role-scoped hydration owns the shared context at a time. Route loads
   * carrying an explicit incident intent outrank background surfaces such as
   * the Active-incidents rail; otherwise two effects mounted in either order
   * can continuously invalidate each other's request token. The owning
   * caller's abort signal is tracked so a later call never coalesces onto a
   * hydration that was already cancelled: React StrictMode's dev double-mount
   * aborts the first effect run before its request settles, and the remount
   * must issue a fresh load instead of adopting the doomed promise.
   */
  private activeRoleLoad: {
    readonly priority: 0 | 1;
    readonly promise: Promise<void>;
    readonly requestToken: number;
    readonly scopeKey: string;
    readonly signal: AbortSignal | undefined;
  } | null = null;
  private inboxRequestToken = 0;
  private regionalSupportEvidenceRequestToken = 0;
  private closureBlockersRequestToken = 0;
  private closureEvidenceRequestToken = 0;
  private boundPrincipalId: string | null = null;
  private activeScopeRoleCode: string | null = null;
  private activeScopeOrganizationUnitId: string | null = null;
  private activeScopeAuthorizationFingerprint: string | null = null;
  private visibilityScopeBound = false;
  /**
   * The scope key the held `incidents` projection was discovered under. Null
   * until discovery completes, and stale the moment the bound scope changes —
   * which is what makes an out-of-scope selection evictable at all.
   */
  private verifiedProjectionScopeKey: string | null = null;
  /**
   * Ids of the last discovery response, recorded the moment it arrives. The
   * committed `incidents` list cannot serve this purpose: selection is verified
   * *during* a load, before the new list is installed, so checking against it
   * would deny the very incident that discovery just authorized.
   */
  private verifiedProjectionIncidentIds: ReadonlySet<string> = new Set();
  /** Prevents background refreshes from undoing an explicit user deselection. */
  private selectionExplicitlyCleared = false;
  /**
   * Kept separate from `selection` so incident-bound modules cannot request a
   * persisted id until the current account has revalidated it in `load()`.
   */
  private preferredStoredIncidentId: string | null = null;

  constructor(
    private readonly api: ApiClient,
    private readonly onMissionChange: (
      missionId: string | null,
      organizationUnitId: string | null,
    ) => void,
    /**
     * Whether the signed-in context holds `account_read`. Asked before the
     * broad account directory is requested at all — a refusal the client can
     * predict should not be discovered by sending the request and reading the
     * 403 off the console. Defaults to permitted so a caller that does not
     * model permissions keeps the previous behaviour.
     */
    private readonly canReadAccountDirectory: () => boolean = () => true,
  ) {
    makeAutoObservable<this, "activeRoleLoad">(
      this,
      { activeRoleLoad: false },
      { autoBind: true },
    );
  }

  /**
   * Bind this shell-scoped store to the authenticated account.
   *
   * Account changes synchronously invalidate in-flight selection work and
   * remove every prior principal's operational projection from memory. The
   * new account's persisted preference remains private until `load()`
   * reauthorizes it. Rebinding the same account is a no-op, so an ordinary
   * capability refresh or work-context switch preserves the selection.
   */
  bindPrincipal(principalId: string | null | undefined): void {
    const normalizedPrincipalId = principalId?.trim() || null;
    if (normalizedPrincipalId === this.boundPrincipalId) return;

    this.contextRequestToken += 1;
    this.inboxRequestToken += 1;
    this.regionalSupportEvidenceRequestToken += 1;
    this.closureBlockersRequestToken += 1;
    this.closureEvidenceRequestToken += 1;
    this.boundPrincipalId = normalizedPrincipalId;
    this.preferredStoredIncidentId = normalizedPrincipalId === null
      ? null
      : readStoredIncidentId(normalizedPrincipalId);
    this.selectionExplicitlyCleared = false;

    this.incidents = [];
    this.regionalChildren = [];
    this.regionalSupportRequests = [];
    this.outgoingRegionalSupport = [];
    this.outgoingRegionalSupportLoading = false;
    this.outgoingRegionalSupportError = null;
    this.regionalCoordinationHandoff = null;
    this.regionalCoordinationHandoffLoading = false;
    this.regionalCoordinationHandoffError = null;
    this.incomingRegionalSupport = [];
    this.incomingRegionalSupportOrganizationUnitId = null;
    this.regionalSupportDetailRequest = null;
    this.regionalSupportDetailSource = null;
    this.regionalSupportDetailIncidentId = null;
    this.regionalSupportEvidenceRequestId = null;
    this.regionalSupportEvidence = [];
    this.regionalSupportEvidenceLoading = false;
    this.regionalSupportEvidenceError = null;
    this.regionalInboxLoading = false;
    this.regionalInboxError = null;
    this.regionalTimeline = [];
    this.regionalTimelineLoading = false;
    this.regionalTimelineError = null;
    this.regionalGapRequirements = [];
    this.regionalGapRequirementsLoading = false;
    this.regionalGapRequirementsError = null;
    this.organizationOptions = [];
    this.closureBlockers = [];
    this.closureBlockersLoading = false;
    this.closureBlockersError = null;
    this.closureEvidence = null;
    this.closureEvidenceLoading = false;
    this.closureEvidenceError = null;
    this.roleAssignments = [];
    this.roleCandidateOptions = [];
    this.accountOptions = [];
    this.context = null;
    this.selection = EMPTY_SELECTION;
    this.loading = false;
    this.error = null;
    this.notice = null;
    this.scopeDeniedIncidentId = null;
    this.onMissionChange(null, null);
  }

  /**
   * Bind incident discovery to the selected work context. A LuK/field context
   * receives only incidents owned by its OV; a Regionalstelle work context
   * receives only its own Regional Parent coordination records. Linked local
   * incidents remain available solely through the parent's consolidated
   * projections (timeline, map and support status), never as selectable work
   * contexts.
   */
  bindOperationalScope(
    roleCode: string | null | undefined,
    organizationUnitId: string | null | undefined,
  ): void {
    const normalizedRoleCode = roleCode?.trim() || null;
    const normalizedOrganizationUnitId = organizationUnitId?.trim() || null;
    const nextScopeOrganizationUnitId = scopedOrganizationUnitId(
      normalizedRoleCode,
      normalizedOrganizationUnitId,
    );
    const scopeChanged =
      !this.visibilityScopeBound ||
      this.activeScopeRoleCode !== normalizedRoleCode ||
      this.activeScopeOrganizationUnitId !== nextScopeOrganizationUnitId;

    const replacesAnEarlierScope = this.visibilityScopeBound;
    this.visibilityScopeBound = true;
    this.activeScopeRoleCode = normalizedRoleCode;
    this.activeScopeOrganizationUnitId = nextScopeOrganizationUnitId;
    if (!scopeChanged) return;
    // Binding a scope for the *first* time evicts nothing: there is no earlier
    // projection to distrust, and clearing here would discard a selection made
    // before the scope was known — which is how a closed incident escaped the
    // closure check on the very next load.
    if (!replacesAnEarlierScope) return;

    this.evictScopeInvalidSelection();
  }

  /**
   * The identity of the scope a discovery projection belongs to. Anything that
   * can change *which* incidents the server would return has to be in here, or
   * a projection fetched under the old answer silently survives the change.
   */
  private get activeScopeKey(): string {
    return [
      this.boundPrincipalId ?? "",
      this.activeScopeRoleCode ?? "",
      this.activeScopeOrganizationUnitId ?? "",
      this.activeScopeAuthorizationFingerprint ?? "",
    ].join("\u0000");
  }

  /**
   * Whether the incidents currently held were discovered under the scope that
   * is bound now. False after an organization, work-context, role/perspective
   * or permission change, until discovery re-runs.
   */
  private get projectionMatchesActiveScope(): boolean {
    return this.verifiedProjectionScopeKey === this.activeScopeKey;
  }

  /**
   * The incident API remains the authoritative visibility boundary: it returns
   * exact-owner incidents, appointed incident scopes and active THW
   * participations, so reapplying an owner-only filter here would discard the
   * latter two valid handoff paths — which is why this used to return a
   * constant `true`.
   *
   * What that missed is that the projection is only authoritative *for the
   * scope it was fetched under*. Once the operator changes organization, work
   * context, role or permissions, the old answer proves nothing: an incident
   * the previous scope could see may be invisible to this one. So visibility
   * requires both a projection that matches the bound scope and membership in
   * it — never a client-side re-derivation of who may see what.
   */
  private incidentVisibleInActiveScope(
    incident: OperationalIncidentSummary,
  ): boolean {
    if (!this.visibilityScopeBound) return true;
    if (!this.projectionMatchesActiveScope) return false;
    if (!this.verifiedProjectionIncidentIds.has(incident.incidentId)) return false;
    return incidentBelongsToOperationalScope(incident, this.activeScopeBinding);
  }

  /** The work context the scope policy judges a projection against. */
  private get activeScopeBinding(): OperationalScopeBinding {
    return {
      organizationUnitId: this.activeScopeOrganizationUnitId,
      roleCode: this.activeScopeRoleCode,
    };
  }

  /**
   * Reduce a discovery response to the incidents that belong to the bound work
   * context. Inert until a scope is bound, so direct `load()` callers and
   * cross-organization administration perspectives keep the server's answer
   * unchanged.
   */
  private narrowToActiveScope(
    discovered: readonly OperationalIncidentSummary[],
  ): readonly OperationalIncidentSummary[] {
    if (!this.visibilityScopeBound) return discovered;
    const binding = this.activeScopeBinding;
    return discovered.filter((incident) =>
      incidentBelongsToOperationalScope(incident, binding));
  }

  /**
   * Fold the authorization the operator holds *now* into the scope identity, so
   * a permission change evicts a selection that permission allowed. Callers
   * pass a stable fingerprint of the effective grants; the store never
   * interprets it.
   */
  bindAuthorizationFingerprint(fingerprint: string | null | undefined): void {
    const normalized = fingerprint?.trim() || null;
    if (this.activeScopeAuthorizationFingerprint === normalized) return;
    this.activeScopeAuthorizationFingerprint = normalized;
    if (!this.visibilityScopeBound) return;
    this.evictScopeInvalidSelection();
  }

  /**
   * Drop everything the previous scope justified. Deliberately does not pick a
   * replacement incident: after an organization, role or permission change the
   * operator must choose again, and auto-selecting would put them into a
   * context they never asked for.
   */
  private evictScopeInvalidSelection(): void {
    this.contextRequestToken += 1;
    // The previous scope's projection proves nothing about this one.
    this.verifiedProjectionScopeKey = null;
    this.verifiedProjectionIncidentIds = new Set();
    const hadSelection = this.selection.incidentId !== null;
    const currentContextRemainsVisible =
      this.context !== null &&
      this.incidentVisibleInActiveScope(this.context.incident);
    this.incidents = this.incidents.filter((incident) =>
      this.incidentVisibleInActiveScope(incident));
    this.regionalChildren = [];
    this.regionalSupportRequests = [];
    this.outgoingRegionalSupport = [];
    this.outgoingRegionalSupportLoading = false;
    this.outgoingRegionalSupportError = null;
    this.regionalCoordinationHandoff = null;
    this.regionalCoordinationHandoffLoading = false;
    this.regionalCoordinationHandoffError = null;
    this.regionalTimeline = [];
    this.regionalTimelineError = null;
    this.regionalGapRequirements = [];
    this.regionalGapRequirementsLoading = false;
    this.regionalGapRequirementsError = null;
    this.closureBlockers = [];
    this.closureEvidence = null;
    if (!currentContextRemainsVisible) {
      this.context = null;
      this.selection = EMPTY_SELECTION;
      // An eviction is not a fresh start: the operator was commanding something
      // and lost it. Suppressing the implicit default keeps the workspace empty
      // once the new scope's projection arrives, so the next incident is one
      // they actually chose. Binding a scope for the first time selects nothing,
      // so it must not arm this — otherwise no context ever loads by default.
      this.selectionExplicitlyCleared = hadSelection;
      this.roleAssignments = [];
      this.roleCandidateOptions = [];
      this.accountOptions = [];
      this.onMissionChange(null, null);
    }
    // The evicted incident is no longer a resumable preference either; leaving
    // it in storage would let a later reload restore what this scope refused.
    if (hadSelection && !currentContextRemainsVisible) {
      this.preferredStoredIncidentId = null;
      writeStoredIncidentId(this.boundPrincipalId, null);
    }
    this.error = null;
    this.notice = null;
    this.scopeDeniedIncidentId = null;
  }

  canSelectIncident(incidentId: string): boolean {
    return this.incidents.some((incident) =>
      incident.incidentId === incidentId &&
      this.incidentVisibleInActiveScope(incident));
  }

  get incident(): OperationalIncidentSummary | null {
    return this.context?.incident ??
      this.incidents.find((item) => item.incidentId === this.selection.incidentId) ??
      null;
  }

  /**
   * The incidents a surface may *offer* as an operational context: everything
   * discovery returned, minus the terminal ones. `incidents` stays the complete
   * authorized projection so an explicit act — a deep link, the closure screen,
   * a reopen — still resolves a finished incident; nothing that lets an operator
   * pick an incident may read that list directly.
   */
  get activeIncidents(): readonly OperationalIncidentSummary[] {
    return this.incidents.filter((item) => isOpenIncidentState(item.state));
  }

  openRegionalSupportDetail(request: RegionalSupportRequest): void {
    this.regionalSupportDetailRequest = request;
    this.regionalSupportDetailSource = this.incomingRegionalSupport.some(
      (candidate) =>
        candidate.supportRequestId === request.supportRequestId,
    )
      ? "INCOMING"
      : "REGIONAL";
    this.regionalSupportDetailIncidentId = this.selection.incidentId;
  }

  closeRegionalSupportDetail(): void {
    this.regionalSupportDetailRequest = null;
    this.regionalSupportDetailSource = null;
    this.regionalSupportDetailIncidentId = null;
    this.regionalSupportEvidenceRequestId = null;
    this.regionalSupportEvidence = [];
    this.regionalSupportEvidenceError = null;
    this.regionalSupportEvidenceLoading = false;
  }

  async loadRegionalSupportEvidence(
    request: RegionalSupportRequest,
    signal?: AbortSignal,
  ): Promise<void> {
    const token = ++this.regionalSupportEvidenceRequestToken;
    this.regionalSupportEvidenceRequestId = request.supportRequestId;
    this.regionalSupportEvidence = [];
    this.regionalSupportEvidenceLoading = true;
    this.regionalSupportEvidenceError = null;
    try {
      const result = await this.api.get<{
        readonly items: readonly DeployableModuleInstance[];
      }>(
        `/v1/deployable-module-instances?organizationUnitId=${encodeURIComponent(
          request.requestedOrganizationUnitId,
        )}`,
        signal,
      );
      const candidates = await Promise.all(result.items.map(async (instance) => {
        const assessment = await this.api.get<StanModuleAssessment>(
          `/v1/deployable-module-instances/${encodeURIComponent(instance.id)}/assessment`,
          signal,
        );
        const readiness = await this.api.get<ReadinessStatusView>(
          `/v1/readiness-status/DEPLOYABLE_MODULE_INSTANCE/${encodeURIComponent(
            instance.id,
          )}?organizationUnitId=${encodeURIComponent(request.requestedOrganizationUnitId)}`,
          signal,
        ).catch((): ReadinessStatusView | null => null);
        const coverage = [
          ...assessment.resourceCoverage,
          ...assessment.qualificationCoverage,
        ];
        return {
          moduleInstanceId: instance.id,
          moduleRevision: instance.revision,
          organizationUnitId: instance.organizationUnitRef,
          unitTypeCode: assessment.unitType.code,
          unitTypeLabel: assessment.unitType.label,
          unitTypeLabelEn: assessment.unitType.labelEn ?? null,
          compatibility: assessment.compatibility,
          assignedPersonnel: assessment.staffing.actualTotal,
          targetPersonnel: assessment.staffing.targetTotal,
          assignedAssets: assessment.assets.length,
          referenceCodes: [...new Set([
            assessment.unitType.code,
            assessment.unitType.id,
            ...assessment.resourceCoverage.map((item) => item.typeRef),
          ].filter((value) => value.trim() !== ""))],
          gaps: coverage
            .filter((item) => item.missing > 0)
            .map((item) => `${item.label}: ${String(item.missing)}`),
          readinessStatus:
            readiness?.effectiveStatus ?? "UNKNOWN_REQUIRES_MANUAL_DECISION",
          readinessSource: readiness?.source ?? "NO_MANUAL_DECISION",
          readinessValidUntil: readiness?.decision?.validUntil ?? null,
        } satisfies RegionalSupportEvidenceCandidate;
      }));
      if (token !== this.regionalSupportEvidenceRequestToken ||
        this.regionalSupportDetailRequest?.supportRequestId !== request.supportRequestId) {
        return;
      }
      runInAction(() => {
        this.regionalSupportEvidence = candidates.sort((left, right) =>
          left.unitTypeLabel.localeCompare(right.unitTypeLabel));
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      if (token !== this.regionalSupportEvidenceRequestToken) return;
      runInAction(() => {
        this.regionalSupportEvidenceError = "REGIONAL_SUPPORT_EVIDENCE_LOAD_FAILED";
      });
    } finally {
      if (token === this.regionalSupportEvidenceRequestToken) {
        runInAction(() => {
          this.regionalSupportEvidenceLoading = false;
        });
      }
    }
  }

  private reconcileRegionalSupportDetail(
    source: "INCOMING" | "REGIONAL",
    requests: readonly RegionalSupportRequest[],
  ): void {
    const selected = this.regionalSupportDetailRequest;
    if (selected === null || this.regionalSupportDetailSource !== source) {
      return;
    }
    const refreshed = requests.find((request) =>
      request.supportRequestId === selected.supportRequestId);
    if (refreshed === undefined) {
      this.closeRegionalSupportDetail();
      return;
    }
    this.regionalSupportDetailRequest = refreshed;
  }

  /** States from which the domain permits transitioning straight to CLOSED. */
  get canCloseIncident(): boolean {
    const state = this.incident?.state;
    return state === "ACTIVE" || state === "CONTAINED" || state === "DEMOBILIZING";
  }

  get canReopenIncident(): boolean {
    return this.incident?.state === "CLOSED";
  }

  /**
   * The selected incident's reported location, assembled from records this
   * store already holds — never a second copy of it.
   *
   * Coordinates come from the persisted intake record; the free-text address
   * falls back to the incident's own `locationText` when the request carries
   * none. `source` reports which record actually answered, so a caller can
   * never present a derived value as if the incident record held it. The
   * incident table has no coordinate columns at all, which is why `source` is
   * `INCIDENT_RECORD` only for the address.
   */
  get incidentLocation(): OperationalIncidentLocation | null {
    const incident = this.incident;
    if (incident === null) return null;
    const located = this.context?.assistanceRequests.find((request) =>
      typeof request.operationalLatitude === "number" &&
      typeof request.operationalLongitude === "number") ?? null;
    const addressed = located ?? this.context?.assistanceRequests.find((request) =>
      request.operationalLocation !== null) ?? null;
    return {
      address: addressed?.operationalLocation ?? incident.locationText,
      incidentId: incident.incidentId,
      incidentNumber: incident.incidentNumber,
      latitude: located?.operationalLatitude ?? null,
      longitude: located?.operationalLongitude ?? null,
      reportedAt: located?.receivedAt ?? null,
      requestId: located?.requestId ?? addressed?.requestId ?? null,
      requestVersion: located?.version ?? addressed?.version ?? null,
      source: located !== null
        ? "INTAKE_REQUEST"
        : addressed?.operationalLocation !== undefined && addressed.operationalLocation !== null
          ? "INTAKE_REQUEST"
          : incident.locationText === null ? null : "INCIDENT_RECORD",
      state: incident.state,
      title: incident.title,
      updatedAt: located?.updatedAt ?? incident.updatedAt,
    };
  }

  /**
   * Persists a confirmed or corrected incident location on the intake record
   * the coordinates belong to, then reloads the context so every surface reads
   * the server's answer rather than an optimistic local edit.
   *
   * Authorization is the server's: the route re-checks
   * `assistance_request_register` in the receiving organization's scope and
   * rejects anything else.
   */
  async correctIncidentLocation(input: {
    readonly latitude: number;
    readonly longitude: number;
    readonly address: string | null;
  }): Promise<boolean> {
    const location = this.incidentLocation;
    const incidentId = this.selection.incidentId;
    if (location === null || location.requestId === null || incidentId === null) return false;
    try {
      // Re-read the version immediately before the write: the projection may be
      // seconds old and the route is optimistic-concurrency guarded.
      const current = await this.api.get<{ readonly version: number }>(
        `/v1/assistance-requests/${encodeURIComponent(location.requestId)}`,
      );
      await this.api.post(
        `/v1/assistance-requests/${encodeURIComponent(location.requestId)}/operational-location`,
        {
          operationalLatitude: input.latitude,
          operationalLocation: input.address,
          operationalLongitude: input.longitude,
        },
        { ifMatch: current.version },
      );
      await this.load(undefined, { preferIncidentId: incidentId });
      return true;
    } catch {
      return false;
    }
  }

  async transitionIncident(targetState: string): Promise<boolean> {
    const incidentId = this.selection.incidentId;
    if (incidentId === null) return false;
    this.error = null;
    try {
      const detail = await this.api.get<{ readonly version: number }>(
        `/v1/incidents/${encodeURIComponent(incidentId)}`,
      );
      await this.api.post(
        `/v1/incidents/${encodeURIComponent(incidentId)}/transitions`,
        { targetState },
        { ifMatch: detail.version },
      );
      // Explicit intent: the operator drove this transition, so they stay on the
      // incident even when it just became terminal.
      await this.load(undefined, { preferIncidentId: incidentId });
      return true;
    } catch {
      runInAction(() => {
        this.error = "INCIDENT_TRANSITION_REJECTED";
      });
      return false;
    }
  }

  /*
   * `closeIncident` and `reopenIncident` used to live here and were left with
   * no caller when the header controls were retired. A lifecycle command that
   * nothing calls is not harmless: it is a second closure implementation
   * waiting to be wired up again, with its own error mapping and its own idea
   * of what a blocker is. Closure and reopening belong to the completion
   * workspace's store, which drives the persisted ending process.
   * `incident-completion-single-surface.test.ts` fails if either reappears.
   */

  async endParticipation(
    participantId: string,
    expectedVersion: number,
  ): Promise<boolean> {
    const incidentId = this.selection.incidentId;
    if (incidentId === null || participantId.trim() === "") return false;
    this.error = null;
    try {
      const ended = await this.api.post<OperationalParticipation>(
        `/v1/incident-participants/${encodeURIComponent(participantId)}/end`,
        {},
        { ifMatch: expectedVersion },
      );
      runInAction(() => {
        if (this.context?.incident.incidentId !== incidentId) return;
        this.context = {
          ...this.context,
          participations: this.context.participations.map((participation) =>
            participation.participantId === ended.participantId
              ? ended
              : participation),
        };
      });
      await this.loadClosureBlockers(incidentId);
      return true;
    } catch (error) {
      const code = error instanceof ApiRequestError &&
        typeof error.problem?.code === "string"
        ? error.problem.code
        : null;
      runInAction(() => {
        this.error = code === "STALE_VERSION"
          ? "PARTICIPATION_END_STALE"
          : code === "FORBIDDEN"
            ? "PARTICIPATION_END_FORBIDDEN"
            : "PARTICIPATION_END_REJECTED";
      });
      return false;
    }
  }

  async addParticipation(
    organizationUnitId: string,
    role: string,
  ): Promise<boolean> {
    const incidentId = this.selection.incidentId;
    if (incidentId === null || organizationUnitId.trim() === "") return false;
    this.error = null;
    try {
      const added = await this.api.post<OperationalParticipation>(
        `/v1/incidents/${encodeURIComponent(incidentId)}/participants`,
        {
          externalName: null,
          joinedAt: null,
          kind: "THW",
          organizationUnitId: organizationUnitId.trim(),
          role: role.trim() || null,
        },
      );
      runInAction(() => {
        if (this.context?.incident.incidentId !== incidentId) return;
        this.context = {
          ...this.context,
          participations: [...this.context.participations, added],
        };
      });
      return true;
    } catch (error) {
      const code = error instanceof ApiRequestError &&
        typeof error.problem?.code === "string"
        ? error.problem.code
        : null;
      runInAction(() => {
        this.error = code === "FORBIDDEN"
          ? "PARTICIPATION_ADD_FORBIDDEN"
          : code === "INVALID_INPUT"
            ? "PARTICIPATION_ADD_INVALID"
            : "PARTICIPATION_ADD_REJECTED";
      });
      return false;
    }
  }

  get supportingLukOptions(): readonly OperationalOrganizationOption[] {
    const occupied = new Set([
      ...this.regionalChildren.map((child) => child.owningOrganizationUnitId),
      ...this.regionalSupportRequests
        .filter((request) =>
          request.status === "REQUESTED" || request.status === "ACCEPTED")
        .map((request) => request.requestedOrganizationUnitId),
    ]);
    return this.organizationOptions
      .filter((unit) =>
        unit.unitTypeCode === "ORTSVERBAND" && !occupied.has(unit.unitId))
      .slice()
      .sort((left, right) => left.label.localeCompare(right.label));
  }

  organizationLabel(organizationUnitId: string): string {
    return this.organizationOptions.find((unit) =>
      unit.unitId === organizationUnitId)?.label ?? organizationUnitId;
  }

  async escalateToRegional(): Promise<boolean> {
    const incident = this.incident;
    if (incident === null || incident.incidentKind !== "LOCAL_LUK") return false;
    this.error = null;
    try {
      const detail = await this.api.get<{ readonly version: number }>(
        `/v1/incidents/${encodeURIComponent(incident.incidentId)}`,
      );
      await this.api.post<{
        readonly regionalIncident: OperationalIncidentSummary;
        readonly localIncident: OperationalIncidentSummary;
      }>(
        `/v1/incidents/${encodeURIComponent(incident.incidentId)}/regional-escalation`,
        {},
        { ifMatch: detail.version },
      );
      // Keep the local LuK operator in the local incident. The Regional Parent
      // is a separate authorization scope and is normally invisible to them.
      await this.load();
      runInAction(() => {
        this.error = null;
        this.notice = "REGIONAL_ESCALATION_CREATED";
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof ApiRequestError &&
          typeof error.problem?.code === "string"
          ? error.problem.code
          : "REGIONAL_ESCALATION_REJECTED";
      });
      return false;
    }
  }

  async requestSupportingLuk(
    owningOrganizationUnitId: string,
    message: string,
    requirementIds: readonly string[],
  ): Promise<boolean> {
    const incident = this.incident;
    if (incident === null ||
      incident.incidentKind !== "REGIONAL_PARENT" ||
      owningOrganizationUnitId.trim() === "" ||
      requirementIds.length === 0) return false;
    this.error = null;
    try {
      await this.api.post(
        `/v1/incidents/${encodeURIComponent(incident.incidentId)}/regional-support-requests`,
        {
          requestedOrganizationUnitId: owningOrganizationUnitId.trim(),
          message: message.trim() || null,
          requirementIds,
        },
      );
      await this.loadRegionalCoordination(incident);
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof ApiRequestError &&
          typeof error.problem?.code === "string"
          ? error.problem.code
          : "SUPPORTING_LUK_REQUEST_REJECTED";
      });
      return false;
    }
  }

  async loadRegionalChildren(regionalIncidentId: string): Promise<void> {
    try {
      const children = await this.api.get<readonly OperationalIncidentSummary[]>(
        `/v1/incidents/${encodeURIComponent(regionalIncidentId)}/regional-children`,
      );
      runInAction(() => {
        this.regionalChildren = children;
      });
    } catch {
      runInAction(() => {
        this.regionalChildren = [];
      });
    }
  }

  async loadRegionalCoordination(incident: OperationalIncidentSummary): Promise<void> {
    runInAction(() => {
      if (this.selection.incidentId !== incident.incidentId) return;
      this.regionalGapRequirementsLoading = true;
      this.regionalGapRequirementsError = null;
      if (incident.incidentKind === "REGIONAL_PARENT") {
        this.regionalTimelineLoading = true;
        this.regionalTimelineError = null;
        this.outgoingRegionalSupportLoading = false;
        this.outgoingRegionalSupportError = null;
        this.regionalCoordinationHandoff = null;
        this.regionalCoordinationHandoffLoading = false;
        this.regionalCoordinationHandoffError = null;
      } else {
        this.outgoingRegionalSupportLoading = true;
        this.outgoingRegionalSupportError = null;
        this.regionalCoordinationHandoffLoading =
          incident.regionalParentIncidentId !== null;
        this.regionalCoordinationHandoffError = null;
        if (incident.regionalParentIncidentId === null) {
          this.regionalCoordinationHandoff = null;
        }
      }
    });
    const directory = await this.api.get<{
      readonly organizationUnits: readonly OperationalOrganizationOption[];
    }>("/v1/admin/management-directory").catch(() => ({
      organizationUnits: [] as const,
    }));
    if (incident.incidentKind === "REGIONAL_PARENT") {
      const [children, requests, timelineResult, requirementsResult] = await Promise.all([
        this.api.get<readonly OperationalIncidentSummary[]>(
          `/v1/incidents/${encodeURIComponent(incident.incidentId)}/regional-children`,
        ).catch(() => [] as const),
        this.api.get<readonly RegionalSupportRequest[]>(
          `/v1/incidents/${encodeURIComponent(incident.incidentId)}/regional-support-requests`,
        ).then(
          (items) => ({ items, loaded: true as const }),
          () => ({
            items: [] as readonly RegionalSupportRequest[],
            loaded: false as const,
          }),
        ),
        this.api.get<readonly RegionalTimelineEntry[]>(
          `/v1/incidents/${encodeURIComponent(incident.incidentId)}/regional-timeline`,
        ).then(
          (entries) => ({ entries, error: null }),
          () => ({
            entries: [] as readonly RegionalTimelineEntry[],
            error: "REGIONAL_TIMELINE_LOAD_FAILED",
          }),
        ),
        this.api.get<readonly OperationalRequirementSummary[]>(
          `/v1/incidents/${encodeURIComponent(incident.incidentId)}/regional-gap-requirements`,
        ).then(
          (items) => ({ items, error: null }),
          () => ({
            items: [] as readonly OperationalRequirementSummary[],
            error: "REGIONAL_GAP_REQUIREMENTS_LOAD_FAILED",
          }),
        ),
      ]);
      runInAction(() => {
        if (this.selection.incidentId !== incident.incidentId) return;
        this.regionalChildren = children;
        this.regionalSupportRequests = requests.items;
        this.outgoingRegionalSupport = [];
        this.outgoingRegionalSupportLoading = false;
        this.outgoingRegionalSupportError = null;
        this.regionalCoordinationHandoff = null;
        this.regionalCoordinationHandoffLoading = false;
        this.regionalCoordinationHandoffError = null;
        if (requests.loaded) {
          this.reconcileRegionalSupportDetail("REGIONAL", requests.items);
        }
        this.regionalTimeline = timelineResult.entries;
        this.regionalTimelineError = timelineResult.error;
        this.regionalTimelineLoading = false;
        this.incomingRegionalSupport = [];
        this.incomingRegionalSupportOrganizationUnitId = null;
        if (requirementsResult.error === null) {
          this.regionalGapRequirements = requirementsResult.items;
        }
        this.regionalGapRequirementsError = requirementsResult.error;
        this.regionalGapRequirementsLoading = false;
        this.organizationOptions = directory.organizationUnits;
      });
      return;
    }
    const handoffRequest = incident.regionalParentIncidentId === null
      ? Promise.resolve({ item: null, error: null } as const)
      : this.api.get<RegionalCoordinationHandoff | null>(
        `/v1/incidents/${encodeURIComponent(incident.incidentId)}/regional-handoff`,
      ).then(
        (item) => !isRegionalCoordinationHandoff(item)
          ? ({ item: null, error: "REGIONAL_HANDOFF_UNAVAILABLE" } as const)
          : ({ item, error: null } as const),
        () => ({
          item: null,
          error: "REGIONAL_HANDOFF_LOAD_FAILED",
        } as const),
      );
    const [outgoing, incoming, requirementsResult, handoffResult] = await Promise.all([
      this.api.get<readonly RegionalSupportRequest[]>(
        `/v1/incidents/${encodeURIComponent(incident.incidentId)}/regional-support-requests`,
      ).then(
        (items) => ({ items, error: null }),
        () => ({
          items: [] as readonly RegionalSupportRequest[],
          error: "OUTGOING_REGIONAL_SUPPORT_LOAD_FAILED",
        }),
      ),
      this.api.get<readonly RegionalSupportRequest[]>(
        `/v1/regional-support-requests?organizationUnitId=${encodeURIComponent(incident.owningOrganizationUnitId)}`,
      ).then(
        (items) => ({ items, loaded: true as const }),
        () => ({
          items: [] as readonly RegionalSupportRequest[],
          loaded: false as const,
        }),
      ),
      this.api.get<readonly OperationalRequirementSummary[]>(
        `/v1/incidents/${encodeURIComponent(incident.incidentId)}/regional-gap-requirements`,
      ).then(
        (items) => ({ items, error: null }),
        () => ({
          items: [] as readonly OperationalRequirementSummary[],
          error: "REGIONAL_GAP_REQUIREMENTS_LOAD_FAILED",
        }),
      ),
      handoffRequest,
    ]);
    runInAction(() => {
      if (this.selection.incidentId !== incident.incidentId) return;
      this.regionalChildren = [];
      this.regionalSupportRequests = [];
      this.outgoingRegionalSupport = outgoing.items;
      this.outgoingRegionalSupportLoading = false;
      this.outgoingRegionalSupportError = outgoing.error;
      if (handoffResult.error === null) {
        this.regionalCoordinationHandoff = handoffResult.item;
      }
      this.regionalCoordinationHandoffLoading = false;
      this.regionalCoordinationHandoffError = handoffResult.error;
      if (outgoing.error === null) {
        this.reconcileRegionalSupportDetail("REGIONAL", outgoing.items);
      }
      this.regionalTimeline = [];
      this.regionalTimelineError = null;
      this.regionalTimelineLoading = false;
      this.incomingRegionalSupport = incoming.items;
      if (incoming.loaded) {
        this.reconcileRegionalSupportDetail("INCOMING", incoming.items);
      }
      this.incomingRegionalSupportOrganizationUnitId =
        incident.owningOrganizationUnitId;
      // The backend projection has already subtracted active COMMITTED and
      // ALLOCATED M10 evidence. Do not fall back to requirement lifecycle state
      // here: SOURCING may be fully covered while awaiting authorized review.
      if (requirementsResult.error === null) {
        this.regionalGapRequirements = requirementsResult.items;
      }
      this.regionalGapRequirementsError = requirementsResult.error;
      this.regionalGapRequirementsLoading = false;
      this.organizationOptions = directory.organizationUnits;
    });
  }

  /**
   * Loads the supporting LuK's organisation inbox independently of an
   * Incident selection. A requested OV must be able to assess and accept the
   * Regionalstelle invitation before its local child Incident exists.
   */
  async loadOrganizationRegionalInbox(
    organizationUnitId: string | null | undefined,
    signal?: AbortSignal,
  ): Promise<void> {
    const requestToken = ++this.inboxRequestToken;
    if (organizationUnitId === null ||
      organizationUnitId === undefined ||
      organizationUnitId.trim() === "") {
      runInAction(() => {
        this.incomingRegionalSupport = [];
        this.incomingRegionalSupportOrganizationUnitId = null;
        this.reconcileRegionalSupportDetail("INCOMING", []);
        this.regionalInboxLoading = false;
        this.regionalInboxError = null;
      });
      return;
    }
    const normalizedOrganizationUnitId = organizationUnitId.trim();
    this.regionalInboxLoading = true;
    this.regionalInboxError = null;
    try {
      const [incoming, directory] = await Promise.all([
        this.api.get<readonly RegionalSupportRequest[]>(
          `/v1/regional-support-requests?organizationUnitId=${encodeURIComponent(
            normalizedOrganizationUnitId,
          )}`,
          signal,
        ),
        this.api.get<{
          readonly organizationUnits: readonly OperationalOrganizationOption[];
        }>("/v1/admin/management-directory", signal).catch(() => ({
          organizationUnits: [] as const,
        })),
      ]);
      if (requestToken !== this.inboxRequestToken) return;
      runInAction(() => {
        this.incomingRegionalSupport = incoming;
        this.incomingRegionalSupportOrganizationUnitId =
          normalizedOrganizationUnitId;
        this.reconcileRegionalSupportDetail("INCOMING", incoming);
        if (directory.organizationUnits.length > 0) {
          this.organizationOptions = directory.organizationUnits;
        }
        this.regionalInboxError = null;
      });
    } catch (error) {
      if (
        requestToken === this.inboxRequestToken &&
        !(error instanceof DOMException && error.name === "AbortError")
      ) {
        runInAction(() => {
          this.incomingRegionalSupport = [];
          this.incomingRegionalSupportOrganizationUnitId =
            normalizedOrganizationUnitId;
          this.regionalInboxError = "REGIONAL_SUPPORT_INBOX_LOAD_FAILED";
        });
      }
    } finally {
      if (requestToken === this.inboxRequestToken) {
        runInAction(() => {
          this.regionalInboxLoading = false;
        });
      }
    }
  }

  async reloadRegionalTimeline(): Promise<void> {
    const incident = this.incident;
    if (incident?.incidentKind !== "REGIONAL_PARENT") return;
    await this.loadRegionalCoordination(incident);
  }

  async loadClosureBlockers(incidentId: string): Promise<void> {
    const requestToken = ++this.closureBlockersRequestToken;
    this.closureBlockersLoading = true;
    this.closureBlockersError = null;
    try {
      const blockers = await this.api.get<readonly IncidentClosureBlocker[]>(
        `/v1/incidents/${encodeURIComponent(incidentId)}/closure-blockers`,
      );
      if (requestToken !== this.closureBlockersRequestToken) return;
      runInAction(() => {
        this.closureBlockers = blockers;
        this.closureBlockersError = null;
      });
    } catch {
      if (requestToken !== this.closureBlockersRequestToken) return;
      runInAction(() => {
        // Preserve the last verified blockers. Replacing them with [] would
        // turn a 403 or transient network failure into false closure readiness.
        this.closureBlockersError = "INCIDENT_CLOSURE_BLOCKERS_LOAD_FAILED";
      });
    } finally {
      if (requestToken === this.closureBlockersRequestToken) {
        runInAction(() => {
          this.closureBlockersLoading = false;
        });
      }
    }
  }

  async loadClosureEvidence(incidentId: string): Promise<void> {
    const requestToken = ++this.closureEvidenceRequestToken;
    this.closureEvidenceLoading = true;
    this.closureEvidenceError = null;
    try {
      const evidence = await this.api.get<IncidentClosureEvidence>(
        `/v1/incidents/${encodeURIComponent(incidentId)}/closure-evidence`,
      );
      if (requestToken !== this.closureEvidenceRequestToken) return;
      runInAction(() => {
        this.closureEvidence = evidence;
        this.closureEvidenceError = null;
      });
    } catch {
      if (requestToken !== this.closureEvidenceRequestToken) return;
      runInAction(() => {
        // Evidence revisions are optimistic-concurrency inputs. Keep the last
        // verified snapshot and make the unknown state explicit.
        this.closureEvidenceError = "INCIDENT_CLOSURE_EVIDENCE_LOAD_FAILED";
      });
    } finally {
      if (requestToken === this.closureEvidenceRequestToken) {
        runInAction(() => {
          this.closureEvidenceLoading = false;
        });
      }
    }
  }

  async recordDamageLossClosureEvidence(input: {
    readonly disposition: "NONE_REPORTED" | "FOLLOW_UP_ASSIGNED";
    readonly reportReference: string;
    readonly followUpOwnerReference: string;
    readonly note: string;
  }): Promise<boolean> {
    const incidentId = this.selection.incidentId;
    if (incidentId === null || input.note.trim() === "") return false;
    try {
      await this.api.post(
        `/v1/incidents/${encodeURIComponent(incidentId)}/closure-evidence/damage-loss`,
        {
          disposition: input.disposition,
          reportReference: input.reportReference.trim() || null,
          followUpOwnerReference: input.followUpOwnerReference.trim() || null,
          note: input.note.trim(),
        },
        { ifMatch: this.closureEvidence?.damageLoss?.revision ?? 0 },
      );
      await Promise.all([
        this.loadClosureEvidence(incidentId),
        this.loadClosureBlockers(incidentId),
      ]);
      return true;
    } catch {
      runInAction(() => {
        this.error = "INCIDENT_CLOSURE_EVIDENCE_REJECTED";
      });
      return false;
    }
  }

  async archiveSituationMap(): Promise<boolean> {
    const incidentId = this.selection.incidentId;
    if (incidentId === null) return false;
    try {
      await this.api.post(
        `/v1/incidents/${encodeURIComponent(incidentId)}/closure-evidence/map-archive`,
        undefined,
        { ifMatch: this.closureEvidence?.situationMapArchive?.revision ?? 0 },
      );
      await Promise.all([
        this.loadClosureEvidence(incidentId),
        this.loadClosureBlockers(incidentId),
      ]);
      return true;
    } catch {
      runInAction(() => {
        this.error = "INCIDENT_MAP_ARCHIVE_REJECTED";
      });
      return false;
    }
  }

  async loadRoleAssignments(incidentId: string): Promise<void> {
    const assignments = await this.api.get<readonly OperationalIncidentRoleAssignment[]>(
      `/v1/incidents/${encodeURIComponent(incidentId)}/roles`,
    ).catch(() => [] as const);
    runInAction(() => {
      if (this.selection.incidentId === incidentId) this.roleAssignments = assignments;
    });
  }

  async loadRoleCandidates(incidentId: string): Promise<void> {
    const candidates = await this.api.get<
      readonly OperationalIncidentRoleCandidate[]
    >(
      `/v1/incidents/${encodeURIComponent(incidentId)}/role-candidates`,
    ).catch(() => [] as const);
    runInAction(() => {
      if (this.selection.incidentId === incidentId) {
        this.roleCandidateOptions = candidates;
      }
    });
  }

  async assignIncidentRole(input: {
    readonly accountId: string;
    readonly roleKey: IncidentRoleKey;
    readonly reason: string;
    readonly startAt: string;
    readonly endAt: string;
  }): Promise<boolean> {
    const incidentId = this.selection.incidentId;
    if (incidentId === null || input.accountId.trim() === "" || input.startAt === "") return false;
    this.error = null;
    try {
      await this.api.post(
        `/v1/incidents/${encodeURIComponent(incidentId)}/roles`,
        {
          accountId: input.accountId.trim(),
          roleKey: input.roleKey,
          reason: input.reason.trim() || null,
          startAt: new Date(input.startAt).toISOString(),
          endAt: input.endAt === "" ? null : new Date(input.endAt).toISOString(),
        },
      );
      await Promise.all([
        this.loadRoleAssignments(incidentId),
        this.loadRoleCandidates(incidentId),
      ]);
      return true;
    } catch (error) {
      runInAction(() => {
        const code = error instanceof ApiRequestError &&
          typeof error.problem?.code === "string"
          ? error.problem.code
          : "INCIDENT_ROLE_ASSIGNMENT_REJECTED";
        this.error = code === "INVALID_INPUT"
          ? "INCIDENT_ROLE_CANDIDATE_INVALID"
          : code === "FORBIDDEN"
            ? "INCIDENT_ROLE_ASSIGNMENT_FORBIDDEN"
            : code;
      });
      return false;
    }
  }

  async endIncidentRole(
    assignment: OperationalIncidentRoleAssignment,
    status: "ENDED" | "REVOKED",
  ): Promise<boolean> {
    this.error = null;
    try {
      await this.api.post(
        `/v1/incident-role-assignments/${encodeURIComponent(assignment.assignmentId)}/end`,
        { status },
        { ifMatch: assignment.version },
      );
      await this.loadRoleAssignments(assignment.incidentId);
      return true;
    } catch {
      runInAction(() => {
        this.error = "INCIDENT_ROLE_END_REJECTED";
      });
      return false;
    }
  }

  async respondToRegionalSupport(
    request: RegionalSupportRequest,
    outcome: "ACCEPTED" | "DECLINED",
    reason: string,
    capabilityResponses: readonly RegionalCapabilityResponse[] = [],
  ): Promise<boolean> {
    this.error = null;
    try {
      const response = await this.api.post<RegionalSupportRequest>(
        `/v1/regional-support-requests/${encodeURIComponent(request.supportRequestId)}/response`,
        {
          outcome,
          reason: reason.trim() || null,
          ...(capabilityResponses.length === 0 ? {} : { capabilityResponses }),
        },
        { ifMatch: request.version },
      );
      await this.load();
      if (response.localIncidentId !== null) await this.selectIncident(response.localIncidentId);
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof ApiRequestError &&
          typeof error.problem?.code === "string"
          ? error.problem.code
          : "REGIONAL_SUPPORT_RESPONSE_REJECTED";
      });
      return false;
    }
  }

  get phase(): string {
    const state = this.incident?.state;
    if (state === undefined) return "INTAKE";
    if (state === "DEMOBILIZING") return "DEMOBILIZATION";
    if (state === "CLOSED" || state === "ARCHIVED") return "CLOSURE";
    if (state === "ACTIVE") return "FIELD_OPERATIONS";
    return "PLANNING";
  }

  async load(
    signal?: AbortSignal,
    options: {
      readonly preferIncidentId?: string | null;
      readonly preferRegionalParent?: boolean;
      readonly preferOwningOrganizationUnitId?: string | null;
    } = {},
  ): Promise<void> {
    const requestToken = ++this.contextRequestToken;
    this.loading = true;
    this.error = null;
    this.notice = null;
    try {
      const discoveredIncidents = await this.api.get<
        readonly OperationalIncidentSummary[]
      >(
        "/v1/incidents?limit=200&offset=0",
        signal,
      );
      if (requestToken !== this.contextRequestToken) return;
      // This response *is* the authorized projection for the scope bound now,
      // so it needs no client-side re-filtering — it needs recording, so that
      // everything derived from it stops being trusted once the scope moves.
      // Discovery decided *readability*; the scope policy decides which of those
      // records belong to the work context bound now. Narrowing here — before
      // anything derives a selection, a deep-link resolution or a stored-id
      // restore from the list — is what stops a foreign incident from reaching
      // a detail request at all.
      const incidents = this.narrowToActiveScope(discoveredIncidents);
      this.verifiedProjectionScopeKey = this.activeScopeKey;
      this.verifiedProjectionIncidentIds = new Set(
        incidents.map((item) => item.incidentId),
      );
      // A stored id is resumed only while its incident is still open. The store
      // cannot tell a deliberate pick from an earlier implicit default — both
      // are persisted the same way — and silently resuming a closed incident
      // after a reload is the worse of the two mistakes. An explicit
      // `preferIncidentId` link still reaches one.
      const restorableStoredIncidentId = this.preferredStoredIncidentId === null
        ? null
        : incidents.find((item) =>
          item.incidentId === this.preferredStoredIncidentId &&
          isOpenIncidentState(item.state))?.incidentId ?? null;
      const explicitIncidentIntentId = options.preferIncidentId?.trim() || null;
      const retainedSelectionId =
        this.selection.incidentId ?? restorableStoredIncidentId;
      // An incident closed *under* the operator must not survive the next
      // refresh. It commands nothing, every panel below it is read-only, and it
      // looks exactly like a live context — so the selection is dropped and a
      // new one has to be made deliberately. Their own closure/reopen act
      // reloads with an explicit `preferIncidentId`, so those screens keep
      // showing the incident they just acted on.
      const retainedSelectionWentTerminal =
        retainedSelectionId !== null &&
        retainedSelectionId !== explicitIncidentIntentId &&
        incidents.some((item) =>
          item.incidentId === retainedSelectionId &&
          !isOpenIncidentState(item.state));
      const candidateSelectionId = retainedSelectionWentTerminal
        ? null
        : retainedSelectionId;
      const selectedStillVisible = incidents.some(
        (item) => item.incidentId === candidateSelectionId,
      );
      const selectedIncident = selectedStillVisible
        ? incidents.find((item) => item.incidentId === candidateSelectionId)
        : undefined;
      const preferredOwningOrganizationUnitId =
        options.preferOwningOrganizationUnitId?.trim() || null;
      const selectedIncidentOwnedByActiveContext =
        selectedIncident !== undefined &&
        (
          preferredOwningOrganizationUnitId === null ||
          selectedIncident.owningOrganizationUnitId ===
            preferredOwningOrganizationUnitId
        )
          ? selectedIncident
          : undefined;
      // Implicit defaulting must never drop an operator into a terminal
      // incident: it holds no resources, every panel below it is read-only,
      // and it looks exactly like a live context. Explicit intent — a
      // `preferIncidentId` deep link, a selection restored from this session —
      // still resolves, so review and reopening stay reachable.
      const incidentsOwnedByActiveContext =
        preferredOwningOrganizationUnitId === null
          ? []
          : incidents.filter((item) =>
            item.owningOrganizationUnitId ===
              preferredOwningOrganizationUnitId &&
            isOpenIncidentState(item.state));
      // A Regionalstelle may be appointed through a tactical function such as
      // INCIDENT_COMMANDER rather than the technical `regionalstelle` access
      // persona. Ownership is therefore the authoritative defaulting signal:
      // if the active organization owns a Regional Parent, prefer that
      // consolidated projection even when visible child incidents sort first.
      const activeContextRegionalParent =
        selectedIncidentOwnedByActiveContext?.incidentKind === "REGIONAL_PARENT"
          ? selectedIncidentOwnedByActiveContext
          : preferOperationalIncident(incidentsOwnedByActiveContext.filter(
            (item) => item.incidentKind === "REGIONAL_PARENT"));
      const preferredRegionalParent = options.preferRegionalParent === true
        ? activeContextRegionalParent ??
          (
            preferredOwningOrganizationUnitId === null
              ? selectedIncident?.incidentKind === "REGIONAL_PARENT"
                ? selectedIncident
                : preferOperationalIncident(incidents.filter((item) =>
                  item.incidentKind === "REGIONAL_PARENT"))
              : undefined
          )
        : activeContextRegionalParent;
      const preferredIncident = options.preferIncidentId === null ||
        options.preferIncidentId === undefined
        ? undefined
        : incidents.find((item) =>
          item.incidentId === options.preferIncidentId);
      const scopeDeniedIncidentId =
        options.preferIncidentId !== null &&
        options.preferIncidentId !== undefined &&
        preferredIncident === undefined
          ? options.preferIncidentId
          : candidateSelectionId !== null && !selectedStillVisible
            ? candidateSelectionId
            : null;
      // Not `[0]`: the projection is newest-first, so the newest *open* incident
      // won — and a report taken minutes ago is open. An incident being worked
      // outranks a fresh one that is only reported; `preferOperationalIncident`
      // documents why, and still lands on a pre-operational incident when that
      // is the only thing this organization has.
      const preferredOwnedIncident =
        selectedIncidentOwnedByActiveContext ??
        preferOperationalIncident(incidentsOwnedByActiveContext);
      const preserveExplicitDeselection =
        this.selectionExplicitlyCleared && options.preferIncidentId == null;
      const nextId = preserveExplicitDeselection || retainedSelectionWentTerminal
        ? null
        : preferredIncident?.incidentId ??
          preferredRegionalParent?.incidentId ??
          preferredOwnedIncident?.incidentId ??
          (selectedStillVisible && preferredOwningOrganizationUnitId === null
            ? candidateSelectionId
            : preferOperationalIncident(incidents)?.incidentId ?? null);
      if (nextId !== null) {
        // A deep link and a selection carried into this load are the operator's
        // intent; anything the ranking produced is only a starting point.
        const selectionOrigin =
          nextId === preferredIncident?.incidentId ||
          nextId === candidateSelectionId
            ? "intended" as const
            : "derived" as const;
        const selected = await this.selectIncidentForRequest(
          nextId,
          signal,
          requestToken,
          selectionOrigin,
        );
        if (!selected || requestToken !== this.contextRequestToken) {
          if (
            requestToken === this.contextRequestToken &&
            this.error === "OPERATIONAL_CONTEXT_SELECTION_FAILED"
          ) {
            runInAction(() => {
              this.error = "OPERATIONAL_CONTEXT_LOAD_FAILED";
            });
          }
          return;
        }
        runInAction(() => {
          this.incidents = incidents;
          this.scopeDeniedIncidentId = scopeDeniedIncidentId;
          this.notice = scopeDeniedIncidentId === null
            ? null
            : "INCIDENT_SCOPE_REDIRECTED";
        });
      }
      else {
        if (
          !retainedSelectionWentTerminal &&
          this.context !== null &&
          this.incidentVisibleInActiveScope(this.context.incident)
        ) {
          // An empty/inconsistent refresh must not destroy an already verified
          // operational context. Keep the current incident and make retry
          // explicit instead of cascading null scope into ETB/map modules.
          // A context that has genuinely finished is the one exception.
          runInAction(() => {
            this.error = "OPERATIONAL_CONTEXT_LOAD_FAILED";
          });
          return;
        }
        runInAction(() => {
          if (requestToken !== this.contextRequestToken) return;
          this.incidents = incidents;
          this.context = null;
          this.selection = EMPTY_SELECTION;
          // Every incident-scoped projection goes with the selection, so no
          // panel can keep rendering the previous incident's records.
          this.regionalChildren = [];
          this.regionalSupportRequests = [];
          this.outgoingRegionalSupport = [];
          this.outgoingRegionalSupportLoading = false;
          this.outgoingRegionalSupportError = null;
          this.regionalCoordinationHandoff = null;
          this.regionalCoordinationHandoffLoading = false;
          this.regionalCoordinationHandoffError = null;
          this.regionalTimeline = [];
          this.regionalTimelineError = null;
          this.regionalTimelineLoading = false;
          this.regionalGapRequirements = [];
          this.regionalGapRequirementsLoading = false;
          this.regionalGapRequirementsError = null;
          this.closureBlockers = [];
          this.closureBlockersError = null;
          this.closureBlockersLoading = false;
          this.closureEvidence = null;
          this.closureEvidenceError = null;
          this.closureEvidenceLoading = false;
          this.roleAssignments = [];
          this.roleCandidateOptions = [];
          this.scopeDeniedIncidentId = scopeDeniedIncidentId;
          this.notice = retainedSelectionWentTerminal
            ? "INCIDENT_CLOSED_SELECTION_CLEARED"
            : scopeDeniedIncidentId === null
              ? null
              : "INCIDENT_SCOPE_REDIRECTED";
          // The operator picks the next incident deliberately: a background
          // refresh must not move a closed workspace onto a different incident.
          if (retainedSelectionWentTerminal) this.selectionExplicitlyCleared = true;
          this.closeRegionalSupportDetail();
        });
        if (requestToken !== this.contextRequestToken) return;
        this.preferredStoredIncidentId = null;
        writeStoredIncidentId(this.boundPrincipalId, null);
        this.onMissionChange(null, null);
      }
    } catch (error: unknown) {
      if (
        requestToken === this.contextRequestToken &&
        !(error instanceof DOMException && error.name === "AbortError")
      ) {
        runInAction(() => {
          this.error = "OPERATIONAL_CONTEXT_LOAD_FAILED";
        });
      }
    } finally {
      if (requestToken === this.contextRequestToken) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  async selectIncident(
    incidentId: string,
    signal?: AbortSignal,
  ): Promise<boolean> {
    if (
      this.visibilityScopeBound &&
      !this.canSelectIncident(incidentId)
    ) {
      runInAction(() => {
        this.scopeDeniedIncidentId = incidentId;
        this.error = "INCIDENT_OUTSIDE_ACTIVE_SCOPE";
      });
      return false;
    }
    const requestToken = ++this.contextRequestToken;
    return this.selectIncidentForRequest(incidentId, signal, requestToken);
  }

  /**
   * Clears the shared incident context without changing the discoverable
   * incident list. Request tokens prevent late responses from repopulating a
   * panel after the operator deselects the incident.
   */
  clearIncidentSelection(): void {
    this.contextRequestToken += 1;
    this.regionalSupportEvidenceRequestToken += 1;
    this.closureBlockersRequestToken += 1;
    this.closureEvidenceRequestToken += 1;
    this.context = null;
    this.selection = EMPTY_SELECTION;
    this.regionalChildren = [];
    this.regionalSupportRequests = [];
    this.outgoingRegionalSupport = [];
    this.outgoingRegionalSupportLoading = false;
    this.outgoingRegionalSupportError = null;
    this.regionalCoordinationHandoff = null;
    this.regionalCoordinationHandoffLoading = false;
    this.regionalCoordinationHandoffError = null;
    this.regionalTimeline = [];
    this.regionalTimelineLoading = false;
    this.regionalTimelineError = null;
    this.regionalGapRequirements = [];
    this.regionalGapRequirementsLoading = false;
    this.regionalGapRequirementsError = null;
    this.closureBlockers = [];
    this.closureBlockersLoading = false;
    this.closureBlockersError = null;
    this.closureEvidence = null;
    this.closureEvidenceLoading = false;
    this.closureEvidenceError = null;
    this.roleAssignments = [];
    this.roleCandidateOptions = [];
    this.loading = false;
    this.error = null;
    this.notice = null;
    this.scopeDeniedIncidentId = null;
    this.closeRegionalSupportDetail();
    this.preferredStoredIncidentId = null;
    this.selectionExplicitlyCleared = true;
    writeStoredIncidentId(this.boundPrincipalId, null);
    this.onMissionChange(null, null);
  }

  /** User-facing selector behaviour: clicking the selected incident clears it. */
  async toggleIncidentSelection(
    incidentId: string,
    signal?: AbortSignal,
  ): Promise<boolean> {
    if (this.selection.incidentId === incidentId) {
      this.clearIncidentSelection();
      return true;
    }
    return this.selectIncident(incidentId, signal);
  }

  /**
   * Whether the operator meant *this* incident (a pick, a deep link, a
   * selection carried forward) or the store merely derived it because
   * something had to be selected. Only the former is worth remembering — a
   * derived default that persists is indistinguishable on the next reload from
   * a deliberate choice, which is how one wrong default becomes sticky for the
   * rest of the session.
   */
  private async selectIncidentForRequest(
    incidentId: string,
    signal: AbortSignal | undefined,
    requestToken: number,
    origin: "derived" | "intended" = "intended",
  ): Promise<boolean> {
    this.loading = true;
    this.error = null;
    try {
      const context = await this.api.get<OperationalIncidentContextView>(
        `/v1/incidents/${encodeURIComponent(incidentId)}/operational-context`,
        signal,
      );
      if (requestToken !== this.contextRequestToken) return false;
      if (
        context.incident.incidentId !== incidentId ||
        !this.incidentVisibleInActiveScope(context.incident)
      ) {
        runInAction(() => {
          this.scopeDeniedIncidentId = incidentId;
          this.error = "INCIDENT_OUTSIDE_ACTIVE_SCOPE";
        });
        return false;
      }
      const missionId =
        context.missionLinks.find((item) => item.relationType === "PRIMARY")
          ?.missionId ??
        context.missionLinks[0]?.missionId ??
        // Compatibility key for modules that still name their operational
        // context column `mission_id`: new workflows are incident-first and
        // use the local incident id until those schemas are migrated.
        context.incident.incidentId;
      if (
        this.regionalSupportDetailRequest !== null &&
        this.regionalSupportDetailIncidentId !== incidentId
      ) {
        this.closeRegionalSupportDetail();
      }
      runInAction(() => {
        this.context = context;
        this.regionalChildren = [];
        this.regionalSupportRequests = [];
        this.outgoingRegionalSupport = [];
        this.outgoingRegionalSupportLoading =
          context.incident.incidentKind === "LOCAL_LUK";
        this.outgoingRegionalSupportError = null;
        this.regionalCoordinationHandoff = null;
        this.regionalCoordinationHandoffLoading =
          context.incident.incidentKind === "LOCAL_LUK" &&
          context.incident.regionalParentIncidentId !== null;
        this.regionalCoordinationHandoffError = null;
        this.incomingRegionalSupport = [];
        this.incomingRegionalSupportOrganizationUnitId = null;
        this.regionalTimeline = [];
        this.regionalTimelineError = null;
        this.regionalTimelineLoading =
          context.incident.incidentKind === "REGIONAL_PARENT";
        this.regionalGapRequirements = [];
        this.regionalGapRequirementsLoading = true;
        this.regionalGapRequirementsError = null;
        this.closureBlockers = [];
        this.closureBlockersError = null;
        this.closureBlockersLoading = false;
        this.closureEvidence = null;
        this.closureEvidenceError = null;
        this.closureEvidenceLoading = false;
        this.roleAssignments = [];
        this.roleCandidateOptions = [];
        this.selection = {
          ...EMPTY_SELECTION,
          incidentId,
          missionId,
          logbookId: context.incident.incidentKind === "LOCAL_LUK" ? `etb-${incidentId}` : null,
        };
        this.selectionExplicitlyCleared = false;
        this.scopeDeniedIncidentId = null;
      });
      if (origin === "intended") {
        this.preferredStoredIncidentId = incidentId;
        writeStoredIncidentId(this.boundPrincipalId, incidentId);
      }
      this.onMissionChange(missionId, context.incident.owningOrganizationUnitId);
      await Promise.all([
        this.loadRegionalCoordination(context.incident),
        this.loadClosureBlockers(context.incident.incidentId),
        context.incident.incidentKind === "LOCAL_LUK"
          ? this.loadClosureEvidence(context.incident.incidentId)
          : Promise.resolve(),
        this.loadRoleAssignments(context.incident.incidentId),
        this.loadRoleCandidates(context.incident.incidentId),
        this.loadAccountOptions(),
      ]);
      return requestToken === this.contextRequestToken;
    } catch (error: unknown) {
      if (
        requestToken === this.contextRequestToken &&
        !(error instanceof DOMException && error.name === "AbortError")
      ) {
        runInAction(() => {
          this.error = "OPERATIONAL_CONTEXT_SELECTION_FAILED";
        });
      }
      return false;
    } finally {
      if (requestToken === this.contextRequestToken) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  selectParticipation(participationId: string | null): void {
    this.selection = { ...this.selection, participationId };
  }

  async loadAccountOptions(): Promise<void> {
    // Operational contexts below administrator level hold no `account_read`, so
    // this directory is a 403 for them. It used to be re-requested on every
    // incident selection and on every panel change; one refusal is enough.
    if (this.accountDirectoryDenied) return;
    // …and the first refusal is one too many when the client already knows the
    // answer. A LuK dispatcher's every session opened with a red 403 in the
    // console for a directory the role is never meant to read, which trains an
    // operator (and whoever reads the log after an incident) to ignore them.
    //
    // Deliberately NOT latched into `accountDirectoryDenied`: this answer is a
    // prediction about the *current* context and it changes when the operator
    // switches to one that does hold `account_read`. The latch is for a real
    // server refusal, which does not.
    if (!this.canReadAccountDirectory()) {
      runInAction(() => { this.accountOptions = []; });
      return;
    }
    try {
      const page = await this.api.get<{ readonly items: readonly OperationalAccountOption[] }>(
        "/v1/accounts?page=1&pageSize=200&status=ACTIVE",
      );
      runInAction(() => {
        this.accountOptions = page.items.filter((account) => account.state === "ACTIVE");
      });
    } catch {
      // The broad account directory is optional. Incident appointments use the
      // narrower, server-validated role-candidate projection instead.
      runInAction(() => {
        this.accountOptions = [];
        this.accountDirectoryDenied = true;
      });
    }
  }

  loadForRole(
    roleCode: string | null | undefined,
    signal?: AbortSignal,
    preferIncidentId?: string | null,
    activeOrganizationUnitId?: string | null,
  ): Promise<void> {
    this.bindOperationalScope(roleCode, activeOrganizationUnitId);
    const scopeKey = [
      this.boundPrincipalId ?? "",
      this.activeScopeRoleCode ?? "",
      this.activeScopeOrganizationUnitId ?? "",
    ].join("\u0000");
    // `null` is explicit too: OperationsRoute deliberately supplies it when
    // the URL has no incident id, whereas background surfaces omit the
    // argument. This lets either effect mount first without the rail
    // superseding route hydration.
    const priority: 0 | 1 = preferIncidentId === undefined ? 0 : 1;
    const activeLoad = this.activeRoleLoad;
    if (
      activeLoad !== null &&
      activeLoad.requestToken === this.contextRequestToken &&
      activeLoad.scopeKey === scopeKey &&
      activeLoad.priority >= priority &&
      activeLoad.signal?.aborted !== true
    ) {
      return activeLoad.promise;
    }

    const load = this.load(signal, {
      ...(preferIncidentId === undefined ? {} : { preferIncidentId }),
      ...(activeOrganizationUnitId === undefined
        ? {}
        : { preferOwningOrganizationUnitId: activeOrganizationUnitId }),
      preferRegionalParent: roleCode === "regionalstelle",
    });
    let trackedLoad: Promise<void>;
    trackedLoad = load.finally(() => {
      if (this.activeRoleLoad?.promise === trackedLoad) {
        this.activeRoleLoad = null;
      }
    });
    this.activeRoleLoad = {
      priority,
      promise: trackedLoad,
      requestToken: this.contextRequestToken,
      scopeKey,
      signal,
    };
    return trackedLoad;
  }

  selectAssistanceRequest(assistanceRequestId: string | null): void {
    this.selection = {
      ...this.selection,
      assistanceRequestId,
      requirementId: null,
    };
  }

  selectMission(missionId: string | null): void {
    if (
      missionId !== null &&
      !this.context?.missionLinks.some((item) => item.missionId === missionId)
    ) {
      return;
    }
    this.selection = { ...this.selection, missionId, operationalNodeId: null };
    this.onMissionChange(
      missionId,
      this.context?.incident.owningOrganizationUnitId ?? null,
    );
  }
}
